import React, { useCallback, useEffect, useRef, useState } from "react";

import PillButton from "components/general/PillButton";
import PlaylistItem from "components/player/PlaylistItem";
// import iconCollapse from "images/ic_expand_less.png";
import iconExpand from "images/ic_expand_more.png";
import styled from "styled-components";
import theme from "styles/theme";

const PlaylistContainer = styled.div`
  padding: ${theme.spacing.unit * 2}px ${theme.spacing.unit * 1}px;

  background-color: ${(props) => props.$backgroundColor};

  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.unit * 1}px;

  border-radius: 0 0 16px 16px;

  position: relative;
`;

const PlaylistItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.unit * 1}px;

  overflow: hidden;

  height: ${(props) => props.$height}px;
  ${(props) =>
    props.$isHeightAnimationEnabled && `transition: height 0.3s ease`};
`;

// Padding needs to be kept in sync with PlaylistItemsContainer
// But it has to be absolute position, so it's tricky
const ShadowPlaylistItemsContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: ${theme.spacing.unit * 1}px;

  position: absolute;
  visibility: hidden;
  pointer-events: none;
  top: 16px;
  left: 8px;
  right: 8px;
`;

const ShowMoreOrLessContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: ${theme.spacing.unit * 1}px;
  box-sizing: border-box;
`;

const Playlist = ({
  brandingData,
  jobClip,
  isExpandable,
  isExpanded,
  onShowMoreClick,
  onShowLessClick,
  playingJobClipTrack,
  onPlaylistItemClick,
  onGetEmbedLinkClick,
  onGoToFullClipClick,
  isHeightAnimationEnabled,
}) => {
  const collapsedPlaylistItemsRef = useRef(null);
  const expandedPlaylistItemsRef = useRef(null);
  const [isCollapsedPlaylistItemsRefSet, setIsCollapsedPlaylistItemsRefSet] =
    useState(false);
  const [isExpandedPlaylistItemsRefSet, setIsExpandedPlaylistItemsRefSet] =
    useState(false);
  const [collapsedPlaylistItemsHeight, setCollapsedPlaylistItemsHeight] =
    useState(0);
  const [expandedPlaylistItemsHeight, setExpandedPlaylistItemsHeight] =
    useState(0);

  const setCollapsedPlaylistItemsRef = useCallback((node) => {
    if (!node) return;

    collapsedPlaylistItemsRef.current = node;
    setIsCollapsedPlaylistItemsRefSet(true);
  }, []);

  const setExpandedPlaylistItemsRef = useCallback((node) => {
    if (!node) return;

    expandedPlaylistItemsRef.current = node;
    setIsExpandedPlaylistItemsRefSet(true);
  }, []);

  useEffect(() => {
    if (
      !isCollapsedPlaylistItemsRefSet ||
      !collapsedPlaylistItemsRef.current ||
      !isExpandedPlaylistItemsRefSet ||
      !expandedPlaylistItemsRef.current
    )
      return;

    const updateHeights = () => {
      if (collapsedPlaylistItemsRef.current) {
        setCollapsedPlaylistItemsHeight(
          collapsedPlaylistItemsRef.current.offsetHeight
        );
      }
      if (expandedPlaylistItemsRef.current) {
        setExpandedPlaylistItemsHeight(
          expandedPlaylistItemsRef.current.offsetHeight
        );
      }
    };

    const resizeObserver = new ResizeObserver(updateHeights);
    resizeObserver.observe(collapsedPlaylistItemsRef.current);
    resizeObserver.observe(expandedPlaylistItemsRef.current);

    // Initial height update
    updateHeights();

    return () => {
      resizeObserver.disconnect();
    };
  }, [jobClip, isCollapsedPlaylistItemsRefSet, isExpandedPlaylistItemsRefSet]);

  return (
    <PlaylistContainer $backgroundColor={brandingData.secondaryStep1Color}>
      {/* Used to calculate the height of the collapsed playlist items */}
      {/* <ShadowPlaylistContainer> */}
      <ShadowPlaylistItemsContainer ref={setCollapsedPlaylistItemsRef}>
        {jobClip.jobClipTracks.slice(0, 3).map((jobClipTrack, i) => (
          <PlaylistItem
            key={jobClipTrack.permalink}
            jobClip={jobClip}
            jobClipTrack={jobClipTrack}
          />
        ))}
      </ShadowPlaylistItemsContainer>
      {/* </ShadowPlaylistContainer> */}
      {/* Used to calculate the height of the expanded playlist items */}
      <ShadowPlaylistItemsContainer ref={setExpandedPlaylistItemsRef}>
        {jobClip.jobClipTracks.map((jobClipTrack, i) => (
          <PlaylistItem
            key={jobClipTrack.permalink}
            jobClip={jobClip}
            jobClipTrack={jobClipTrack}
          />
        ))}
      </ShadowPlaylistItemsContainer>
      <PlaylistItemsContainer
        $height={
          isExpanded || !isExpandable
            ? expandedPlaylistItemsHeight
            : collapsedPlaylistItemsHeight
        }
        $isHeightAnimationEnabled={isHeightAnimationEnabled}
      >
        <>
          {jobClip.jobClipTracks.map((jobClipTrack, i) => {
            // Always show all items, but use height from shadow elements to cut them off
            // if (isExpandable && !isExpanded && i > 2) {
            //   return null;
            // }

            let isJobClipTrackActive = false;
            if (
              playingJobClipTrack &&
              jobClipTrack.permalink === playingJobClipTrack.permalink
            ) {
              isJobClipTrackActive = true;
            }

            return (
              <PlaylistItem
                key={jobClipTrack.permalink}
                isActive={isJobClipTrackActive}
                jobClip={jobClip}
                jobClipTrack={jobClipTrack}
                onClick={onPlaylistItemClick(jobClipTrack)}
                onGetEmbedLinkClick={onGetEmbedLinkClick(
                  jobClipTrack.permalink
                )}
                onGoToFullClipClick={onGoToFullClipClick}
              />
            );
          })}
        </>
      </PlaylistItemsContainer>
      {jobClip.jobClipTracks.length > 4 && isExpandable && (
        <ShowMoreOrLessContainer key={"show-more-or-less-button"}>
          <PillButton
            isSmall
            isBrandedGhost
            brandingData={brandingData}
            iconTrailing={iconExpand}
            iconTrailingTransform={
              isExpanded ? "rotate(-180deg)" : "rotate(0deg)"
            }
            onClick={isExpanded ? onShowLessClick : onShowMoreClick}
          >
            {isExpanded ? "Show less" : "Show more"}
          </PillButton>
        </ShowMoreOrLessContainer>
      )}
    </PlaylistContainer>
  );
};

export default Playlist;
